import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdCheck, MdFilterAlt, MdFilterAltOff, MdSearch } from 'react-icons/md';

interface ColumnFilterDropdownProps {
    label: string;
    /** Valores únicos disponíveis para a coluna. */
    values: any[];
    /** Valores atualmente selecionados. */
    activeValues: any[];
    /** Quantidade de linhas por valor, exibida ao lado da opção. */
    counts?: Map<any, number>;
    onToggleValue: (value: any, checked: boolean) => void;
    onSetValues: (values: any[]) => void;
    onClear: () => void;
    emptyValueToken: unknown;
    emptyValueLabel: string;
}

/**
 * AF-3418: substitui o dropdown de checkbox montado à mão no cabeçalho da grid.
 *
 * O anterior tinha três limitações práticas: listava todos os valores únicos sem busca
 * (colunas de bureau chegam a centenas de valores distintos), não tinha "selecionar
 * todos", e só fechava pelo botão "Fechar" — clicar fora deixava o painel aberto por cima
 * da tabela.
 */
const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({
    label,
    values,
    activeValues,
    counts,
    onToggleValue,
    onSetValues,
    onClear,
    emptyValueToken,
    emptyValueLabel,
}) => {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const hasActiveFilter = activeValues.length > 0;

    // Fecha ao clicar fora ou com Escape
    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const labelFor = (value: any): string =>
        value === emptyValueToken ? emptyValueLabel : String(value);

    const visibleValues = useMemo(() => {
        const normalized = term.trim().toLowerCase();
        if (!normalized) return values;
        return values.filter((value) => labelFor(value).toLowerCase().includes(normalized));
        // labelFor depende só de props estáveis
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, term, emptyValueToken, emptyValueLabel]);

    const allVisibleSelected =
        visibleValues.length > 0 && visibleValues.every((value) => activeValues.includes(value));

    return (
        <div className="relative inline-flex" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-slate-600 ${
                    hasActiveFilter
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-400 dark:text-gray-500'
                }`}
                title={hasActiveFilter ? `Filtrando ${label}` : `Filtrar ${label}`}
                aria-label={`Filtrar ${label}`}
                aria-expanded={open}
            >
                {hasActiveFilter ? <MdFilterAlt size={16} /> : <MdFilterAltOff size={16} />}
            </button>

            {hasActiveFilter && (
                <span className="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                    {activeValues.length}
                </span>
            )}

            {open && (
                <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-xl dark:border-slate-600 dark:bg-slate-700">
                    <div className="border-b border-gray-200 p-2 dark:border-slate-600">
                        <div className="relative">
                            <MdSearch
                                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                                size={16}
                                aria-hidden="true"
                            />
                            <input
                                type="text"
                                value={term}
                                onChange={(event) => setTerm(event.target.value)}
                                placeholder={`Buscar em ${label}`}
                                aria-label={`Buscar valores de ${label}`}
                                autoFocus
                                className="h-8 w-full rounded border border-gray-300 bg-white pl-7 pr-2 text-sm normal-case text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-500 dark:bg-slate-600 dark:text-gray-100"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between border-b border-gray-200 px-2 py-1.5 text-xs dark:border-slate-600">
                        <button
                            type="button"
                            onClick={() =>
                                onSetValues(
                                    allVisibleSelected
                                        ? activeValues.filter((value) => !visibleValues.includes(value))
                                        : Array.from(new Set([...activeValues, ...visibleValues]))
                                )
                            }
                            className="font-medium normal-case text-blue-600 hover:underline dark:text-blue-400"
                        >
                            {allVisibleSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                        </button>
                        <span className="normal-case text-gray-500 dark:text-gray-400">
                            {visibleValues.length} {visibleValues.length === 1 ? 'valor' : 'valores'}
                        </span>
                    </div>

                    <div className="max-h-60 overflow-y-auto py-1">
                        {visibleValues.length === 0 && (
                            <p className="px-3 py-4 text-center text-sm normal-case text-gray-500 dark:text-gray-400">
                                Nenhum valor encontrado
                            </p>
                        )}

                        {visibleValues.map((value) => {
                            const checked = activeValues.includes(value);
                            const count = counts?.get(value);
                            return (
                                <label
                                    key={value === emptyValueToken ? '__empty_key__' : String(value)}
                                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-600"
                                >
                                    <span
                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                            checked
                                                ? 'border-blue-600 bg-blue-600 text-white'
                                                : 'border-gray-300 bg-white dark:border-slate-400 dark:bg-slate-500'
                                        }`}
                                    >
                                        {checked && <MdCheck size={12} />}
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={checked}
                                        onChange={(event) => onToggleValue(value, event.target.checked)}
                                    />
                                    <span
                                        className="flex-1 truncate text-sm normal-case text-gray-700 dark:text-gray-200"
                                        title={labelFor(value)}
                                    >
                                        {value === emptyValueToken ? (
                                            <em className="text-gray-400">{emptyValueLabel}</em>
                                        ) : (
                                            labelFor(value)
                                        )}
                                    </span>
                                    {count !== undefined && (
                                        <span className="shrink-0 rounded bg-gray-100 px-1.5 text-xs normal-case text-gray-600 dark:bg-slate-600 dark:text-gray-300">
                                            {count}
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>

                    <div className="flex gap-2 border-t border-gray-200 p-2 dark:border-slate-600">
                        <button
                            type="button"
                            onClick={() => {
                                onClear();
                                setTerm('');
                            }}
                            disabled={!hasActiveFilter}
                            className="flex-1 rounded border border-gray-300 px-3 py-1 text-sm normal-case text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-500 dark:text-gray-200 dark:hover:bg-slate-600"
                        >
                            Limpar
                        </button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="flex-1 rounded bg-blue-600 px-3 py-1 text-sm normal-case text-white hover:bg-blue-700"
                        >
                            Aplicar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnFilterDropdown;
